/**
 * Recovery for stale dynamic-import chunks after a deploy.
 *
 * @module lib/chunkReloadRecovery
 *
 * @remarks
 * The app code-splits every route into a content-hashed chunk, and each deploy
 * ships a new set of hashes while discarding the previous build's files. A tab
 * that was open across a deploy then navigates to a route whose chunk no longer
 * exists on the server, the dynamic `import()` 404s, and — with nothing to catch
 * an async import failure — the page goes blank.
 *
 * This installs a last-resort handler: when a dynamic import fails, reload once
 * so the browser fetches the current `index.html` (served `no-store`) and its
 * live chunk hashes. A sessionStorage cooldown prevents a reload loop when a
 * chunk is genuinely gone (e.g. a route removed in the new build) rather than
 * merely stale.
 */

const RELOAD_GUARD_KEY = "loa:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

// Wording used across Chromium, Firefox, and Safari for a failed dynamic import.
const CHUNK_ERROR_PATTERN = new RegExp(
  [
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "ChunkLoadError",
  ].join("|"),
  "i",
);

/**
 * True when `message` looks like a failed dynamic-import / chunk load, across
 * the wording used by Chromium, Firefox, and Safari.
 */
export function isChunkLoadError(message: string): boolean {
  return CHUNK_ERROR_PATTERN.test(message);
}

function hasRecentlyReloaded(now: number): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);

    if (raw === null) {
      return false;
    }

    const last = Number.parseInt(raw, 10);

    return Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS;
  } catch (err) {
    // sessionStorage can throw (private mode / storage disabled). Treat it as
    // "no prior reload" so recovery still fires; a reload can't loop faster than
    // the browser navigates, so the missing guard degrades gracefully.
    console.warn("[chunkReload] Unable to read reload guard.", err);

    return false;
  }
}

function markReloaded(now: number): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch (err) {
    console.warn("[chunkReload] Unable to record reload guard.", err);
  }
}

/**
 * Decides whether to recover from a chunk failure now, and records the attempt.
 *
 * Returns `true` (and stamps the guard) when no recovery reload has happened
 * within {@link RELOAD_COOLDOWN_MS}; returns `false` when one just did, so the
 * caller must NOT reload again — this is the loop breaker.
 *
 * Exported for unit testing; prefer {@link installChunkReloadRecovery} at
 * runtime.
 */
export function shouldRecoverFromStaleChunk(now: number): boolean {
  if (hasRecentlyReloaded(now)) {
    return false;
  }

  markReloaded(now);

  return true;
}

function recover(reason: string): void {
  const now = Date.now();

  if (!shouldRecoverFromStaleChunk(now)) {
    console.error(
      `[chunkReload] Reload loop guard tripped (${reason}); not reloading. ` +
        "The chunk is likely gone in the new build, not merely stale.",
    );

    return;
  }

  console.warn(
    `[chunkReload] A dynamic import failed (${reason}); reloading to fetch the current build.`,
  );
  window.location.reload();
}

/**
 * Installs the chunk-reload recovery handlers on `window`. Call once during
 * bootstrap, after the entry bundle has successfully loaded.
 */
export function installChunkReloadRecovery(): void {
  if (typeof window === "undefined") {
    return;
  }

  // Vite's preload helper fires this when a modulepreload/dynamic import 404s.
  // Prevent the default (Vite rethrows) so our reload is the handler of record.
  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    recover("vite:preloadError");
  });

  // Safety net for a dynamic import that rejects outside the preload helper
  // (e.g. a route navigation). Scoped to chunk-load messages so unrelated
  // rejections never trigger a reload.
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);

    if (!isChunkLoadError(message)) {
      return;
    }

    recover("unhandledrejection");
  });
}
